import { FieldProps } from "@rjsf/utils";
import React, { useEffect, useState } from "react";

import { Box, Divider, FormControl, InputLabel, MenuItem, Select, Typography } from "@mui/material";

import { JSONSchema7 } from "json-schema";

import { JsonSchemaViewer } from "../../json-schema-viewer";
import useReactFlowHelpers from "../hooks/useReactFlowHelpers";

type SelectedVariableType = {
  variable_value: HTMLInputElement | null;
  variable_type: string;
  variable_path: string;
  nodeId: string;
};

export type SchemaSourceType = 'root' | 'previous';
const schemaSourceOptions: (SchemaSourceType)[] = ['root', 'previous'];

// const isSchemaValid = (sch: JSONSchema7): boolean => {
//   return sch && typeof sch === 'object' && Object.keys(sch).length > 0;
// }

const mergeSchemas = (existingSchema: JSONSchema7, newSchema: JSONSchema7): JSONSchema7 => {
  // Create a deep clone of the existing schema to avoid mutations
  const mergedSchema: JSONSchema7 = JSON.parse(JSON.stringify(existingSchema));

  // Merge properties
  if (newSchema.properties) {
    mergedSchema.properties = {
      ...mergedSchema.properties,
      ...newSchema.properties,
    };
  }

  // Recursively merge nested properties
  if (newSchema.properties && mergedSchema.properties) {
    for (const key in newSchema.properties) {
      if (existingSchema.properties?.[key] && newSchema.properties[key]) {
        mergedSchema.properties[key] = mergeSchemas(
          existingSchema.properties[key] as JSONSchema7,
          newSchema.properties[key] as JSONSchema7
        );
      }
    }
  }

  // Combine required arrays
  const existingRequired = existingSchema.required || [];
  const newRequired = newSchema.required || [];
  mergedSchema.required = Array.from(new Set([...existingRequired, ...newRequired]));

  return mergedSchema;
}


const CustomJsonSchemaField: React.FC<FieldProps> = (props) => {

  const { getRequestNodeSchema, getParentNode } = useReactFlowHelpers();

  const updateRootNodeSchemaOnSubmit = props.uiSchema?.['ui:options']?.updateRootNodeSchemaOnSubmit as (sch: JSONSchema7) => void;

  // const sourceSchema = (props.uiSchema?.["ui:options"]?.["sourceSchema"] as JSONSchema) ?? {};
  // const targetSchema = (props.uiSchema?.["ui:options"]?.["targetSchema"] as JSONSchema) ?? {};
  const [sourceSchema, setSourceSchema] = useState<JSONSchema7>({});
  const [targetSchema, setTargetSchema] = useState<JSONSchema7>({});
  const [previousNodeId, setPreviousNodeId] = useState<string>('');
  const [schemaSourceValue, setSchemaSourceValue] = useState<SchemaSourceType>(schemaSourceOptions[0]);
  const [selectedVariable, setSelectedVariable] = useState<SelectedVariableType>({
    variable_value: null,
    variable_type: "",
    variable_path: "",
    nodeId: `${props.uiSchema?.["ui:options"]?.nodeId}` || ""
  });


  useEffect(() => {
    const currNodeId = props.uiSchema?.["ui:options"]?.nodeId as string;
    const prevNode = getParentNode(currNodeId)
    setPreviousNodeId(prevNode?.id || '');

    return () => { }
  }, [getParentNode, props.uiSchema])


  useEffect(() => {
    console.log('---------updating source and target schema---------')
    setSourceSchema((props.uiSchema?.["ui:options"]?.["sourceSchema"] as JSONSchema7) ?? {});
    setTargetSchema((props.uiSchema?.["ui:options"]?.["targetSchema"] as JSONSchema7) ?? {});

    return () => { }
  }, [props.uiSchema]);

  useEffect(() => {
    if (schemaSourceValue === 'root') {
      const existingSchema = getRequestNodeSchema();
      console.log('get request node data------------>>>> ', existingSchema);

      const merged_schema = mergeSchemas(existingSchema, targetSchema);
      updateRootNodeSchemaOnSubmit && updateRootNodeSchemaOnSubmit(merged_schema);
      setSourceSchema(merged_schema);
    }
    else {
      setSourceSchema((props.uiSchema?.["ui:options"]?.["sourceSchema"] as JSONSchema7) ?? {});
    }

    return () => { }
  }, [getRequestNodeSchema, props.uiSchema, schemaSourceValue, targetSchema, updateRootNodeSchemaOnSubmit]);


  console.log("In CustomJsonSchemaField", schemaSourceValue);

  // console.log('source schema isValid: ', isSchemaValid(sourceSchema), sourceSchema);
  // console.log('target schema isValid: ', isSchemaValid(targetSchema), targetSchema);  

  return (
    <Box>
      <Typography component="p" my="10px">
        {props.schema?.title}
      </Typography>

      <Divider sx={{ mb: 1 }} />

      <Typography variant="caption" textAlign="left" mb={2} color="textSecondary">
        {props.schema?.description}
      </Typography>

      <Box sx={{ display: "flex", gap: "16px", maxHeight: "70vh", overflow: "auto" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" mb={2} display='flex' alignItems='center' height='50px'>Target Schema</Typography>

          <JsonSchemaViewer
            schema={targetSchema}
            emptyText="Target schema not available."
            defaultExpandedDepth={Infinity}
            viewMode="write"
            renderRootTreeLines={true}
            selectedVariable={selectedVariable}
            setSelectedVariable={setSelectedVariable}
            sourceSchemaDestination={schemaSourceValue}
            previousNodeId={previousNodeId}
          />
        </Box>

        <Box sx={{ flex: 1 }}>
          <Box mb={2} display='flex' alignItems='center' justifyContent='space-between' height='50px'>
            <Typography variant="body2">Source Schema</Typography>

            <FormControl size="small" sx={{ width: '160px' }}>
              <InputLabel id="Schema-Source-select-label">Schema Source</InputLabel>
              <Select
                labelId="Schema-Source-select-label"
                id="Schema-Source-simple-select"
                value={schemaSourceValue}
                label="Schema Source"
                onChange={e => setSchemaSourceValue(e.target.value as SchemaSourceType)}
              >
                {schemaSourceOptions.map(item => (
                  <MenuItem key={item} value={item}>{item}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <JsonSchemaViewer
            schema={sourceSchema}
            emptyText="Source schema not available."
            defaultExpandedDepth={Infinity}
            viewMode="standalone"
            renderRootTreeLines={true}
            selectedVariable={selectedVariable}
            setSelectedVariable={setSelectedVariable}
            sourceSchemaDestination={schemaSourceValue}
            previousNodeId={previousNodeId}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default CustomJsonSchemaField;
